/**
 * Project Name: Brainsie eCommerce v4
 * Component: Section Renderer
 * Description: Renders individual sections with their components
 * Author: Brainsie Team
 * Date Created: October 24, 2025
 */

import React from "react";
import { Box, Container } from "@mui/material";
import ComponentRenderer from "./ComponentRenderer";
import { useSectionData } from "./hooks/useSectionData";

// Import section components
import InfographicSectionDefault from "./components/InfographicSectionDefault";
import InfographicSectionAlternative from "./components/InfographicSectionAlternative";
import GridLayout3Column from "./components/GridLayout3Column";
import TabLayout from "./components/TabLayout";

// Import dynamic section components
import AllCategoriesSection from "./sections/AllCategoriesSection";
import FeaturedProductsSection from "./sections/FeaturedProductsSection";
import BestSellersSectionWrapper from "./sections/BestSellersSectionWrapper";
import PopularProductsSection from "./sections/PopularProductsSection";
import FaqViewSection from "./sections/FaqViewSection";

// Section component mapping by Section_Master_Id only
const SECTION_COMPONENTS = {
  1: InfographicSectionDefault, // Infographic Section Default - SEC_001
  2: InfographicSectionAlternative, // Infographic Section Alternative - SEC_002
  3: GridLayout3Column, // Grid Layout 3 Column - SEC_003
  4: TabLayout, // Tab Layout - SEC_004
  5: AllCategoriesSection, // All Categories - SEC_005
  6: FeaturedProductsSection, // Featured Products - SEC_006
  7: BestSellersSectionWrapper, // Best Sellers - SEC_007
  8: PopularProductsSection, // Popular Products - SEC_008
  9: FaqViewSection, // FAQ View - SEC_009
};

export default function SectionRenderer({
  section,
  pageData,
  darkMode,
  theme,
  headerRefresh,
  setHeaderRefresh,
}) {
  const { components, loading, error } = useSectionData(section.Section_Id);

  // Get section settings with comprehensive defaults
  const sectionSettings = section.Section_Settings
    ? JSON.parse(section.Section_Settings)
    : {
        paddingY: 8,
        paddingX: 0,
        maxWidth: "lg",
        backgroundColor: "",
        textColor: "",
        headingColor: "",
        subHeadingColor: "",
        bodyColor: "",
        overlayColor: "rgba(0, 0, 0, 0.5)",
        enableBackgroundImage: false,
        textAlign: "left",
        customStyles: "",
      };

  // Get section component by Section_Master_Id only
  let SectionComponent = SECTION_COMPONENTS[section.Section_Master_Id];
  // console.log("🔍 SECTION RENDERER - ID MAPPING:", {
  //   sectionId: section.Section_Id,
  //   masterId: section.Section_Master_Id,
  //   selectedComponent: SectionComponent?.name || "NOT FOUND",
  // });

  // Comprehensive section styling functions
  const getSectionStyles = () => {
    const baseStyles = {
      py: sectionSettings.paddingY || 4, // Default 4 theme units
      px: sectionSettings.paddingX || 0, // Default 0 horizontal padding
      position: "relative",
      textAlign: sectionSettings.textAlign || "left",
      color:
        sectionSettings.textColor ||
        (darkMode ? theme.palette.common.white : theme.palette.text.primary),
    };

    // Background handling
    if (sectionSettings.backgroundColor) {
      baseStyles.backgroundColor = sectionSettings.backgroundColor;
    } else {
      baseStyles.backgroundColor = darkMode
        ? theme.palette.grey[800]
        : theme.palette.background.paper;
    }

    // Background image handling
    if (sectionSettings.enableBackgroundImage && section.Section_Media) {
      baseStyles.backgroundImage = `url(${process.env.REACT_APP_BASEURL || ""}${
        section.Section_Media
      })`;
      baseStyles.backgroundSize = "cover";
      baseStyles.backgroundPosition = "center";
      baseStyles.backgroundRepeat = "no-repeat";

      // Add overlay if specified
      if (sectionSettings.overlayColor) {
        baseStyles["&::before"] = {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: sectionSettings.overlayColor,
          zIndex: 1,
        };
        baseStyles["& > *"] = {
          position: "relative",
          zIndex: 2,
        };
      }
    }

    // Parse and apply custom styles
    if (sectionSettings.customStyles) {
      try {
        const customStyles =
          typeof sectionSettings.customStyles === "string"
            ? JSON.parse(sectionSettings.customStyles)
            : sectionSettings.customStyles;
        Object.assign(baseStyles, customStyles);
      } catch (error) {
        console.warn("Invalid custom styles:", sectionSettings.customStyles);
      }
    }

    return baseStyles;
  };

  // Get specific color values with fallbacks
  const getHeadingColor = () =>
    sectionSettings.headingColor ||
    sectionSettings.textColor ||
    (darkMode ? theme.palette.common.white : theme.palette.text.primary);

  const getSubHeadingColor = () =>
    sectionSettings.subHeadingColor ||
    sectionSettings.textColor ||
    (darkMode ? theme.palette.grey[300] : theme.palette.text.secondary);

  const getBodyColor = () =>
    sectionSettings.bodyColor ||
    sectionSettings.textColor ||
    (darkMode ? theme.palette.grey[400] : theme.palette.text.primary);

  // Fallback to generic section renderer with full settings integration
  if (!SectionComponent) {
    return (
      <Box
        component="section"
        className={`section-${section.Section_Master_Code?.toLowerCase()}`}
        sx={getSectionStyles()}
      >
        <Container
          maxWidth={sectionSettings.maxWidth || "lg"}
          sx={{
            textAlign: sectionSettings.textAlign || "left",
          }}
        >
          {/* Section Header with comprehensive styling */}
          {(section.Section_Heading ||
            section.Section_Sub_Heading ||
            section.Section_Sub_Text) && (
            <Box
              sx={{
                mb: 4,
                textAlign: sectionSettings.textAlign || "left",
              }}
            >
              {section.Section_Heading && (
                <h2
                  style={{
                    color: getHeadingColor(),
                    margin: "0 0 16px 0",
                    fontSize: "2.125rem",
                    fontWeight: "600",
                    lineHeight: 1.2,
                    textAlign: sectionSettings.textAlign || "left",
                  }}
                >
                  {section.Section_Heading}
                </h2>
              )}

              {section.Section_Sub_Heading && (
                <h3
                  style={{
                    color: getSubHeadingColor(),
                    margin: "0 0 12px 0",
                    fontSize: "1.5rem",
                    fontWeight: "500",
                    lineHeight: 1.3,
                    textAlign: sectionSettings.textAlign || "left",
                  }}
                >
                  {section.Section_Sub_Heading}
                </h3>
              )}

              {section.Section_Sub_Text && (
                <p
                  style={{
                    color: getBodyColor(),
                    margin: "0",
                    fontSize: "1rem",
                    lineHeight: 1.6,
                    maxWidth: "800px",
                    marginLeft:
                      sectionSettings.textAlign === "center" ? "auto" : "0",
                    marginRight:
                      sectionSettings.textAlign === "center" ? "auto" : "0",
                    textAlign: sectionSettings.textAlign || "left",
                  }}
                >
                  {section.Section_Sub_Text}
                </p>
              )}
            </Box>
          )}

          {/* Section Media (if not used as background) */}
          {section.Section_Media && !sectionSettings.enableBackgroundImage && (
            <Box sx={{ mb: 4, textAlign: sectionSettings.textAlign || "left" }}>
              <img
                src={`${process.env.REACT_APP_BASEURL || ""}${
                  section.Section_Media
                }`}
                alt={section.Section_Heading || "Section media"}
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              />
            </Box>
          )}

          {/* Render Components with section context */}
          {loading ? (
            <Box
              sx={{
                color: getBodyColor(),
                textAlign: "center",
                py: 4,
                fontSize: "1rem",
              }}
            >
              Loading components...
            </Box>
          ) : error ? (
            <Box
              sx={{
                color: theme.palette.error.main,
                textAlign: "center",
                py: 4,
                fontSize: "1rem",
              }}
            >
              Error loading components: {error.message}
            </Box>
          ) : (
            <Box>
              {components?.map((component) => (
                <ComponentRenderer
                  key={component.Component_Id}
                  component={component}
                  section={section}
                  sectionSettings={sectionSettings}
                  darkMode={darkMode}
                  theme={theme}
                />
              ))}
            </Box>
          )}
        </Container>
      </Box>
    );
  }

  // Render using specific section component with enhanced props
  return (
    <SectionComponent
      section={section}
      components={components}
      loading={loading}
      error={error}
      pageData={pageData}
      darkMode={darkMode}
      theme={theme}
      sectionSettings={sectionSettings}
      getSectionStyles={getSectionStyles}
      getHeadingColor={getHeadingColor}
      getSubHeadingColor={getSubHeadingColor}
      getBodyColor={getBodyColor}
      headerRefresh={headerRefresh}
      setHeaderRefresh={setHeaderRefresh}
    />
  );
}
